import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import {
  getHistoryCaptureInterval,
  updateHistoryCaptureInterval
} from '../../v2/services/api/settings.api';

export const HISTORY_INTERVAL_OPTIONS = [
  { minutes: 60, label: 'Every hour' },
  { minutes: 120, label: 'Every 2 hours' },
  { minutes: 240, label: 'Every 4 hours' },
  { minutes: 360, label: 'Every 6 hours' },
  { minutes: 720, label: 'Every 12 hours' },
  { minutes: 1440, label: 'Every 24 hours' }
];

const responseData = (response) => response?.data?.data ?? response?.data ?? response;

const HistoricalDataSettings = () => {
  const [intervalMinutes, setIntervalMinutes] = useState(1440);
  const [savedIntervalMinutes, setSavedIntervalMinutes] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    getHistoryCaptureInterval()
      .then((response) => {
        if (!active) return;
        const data = responseData(response);
        setIntervalMinutes(data.historyCaptureIntervalMinutes);
        setSavedIntervalMinutes(data.historyCaptureIntervalMinutes);
        setStatus('ready');
      })
      .catch(() => {
        if (!active) return;
        setError('Historical data frequency could not be loaded.');
        setStatus('error');
      });
    return () => {
      active = false;
    };
  }, []);

  const save = async () => {
    setStatus('saving');
    setError(null);
    try {
      await updateHistoryCaptureInterval(intervalMinutes);
      setSavedIntervalMinutes(intervalMinutes);
      setStatus('saved');
    } catch (_error) {
      setError('Historical data frequency could not be saved.');
      setStatus('error');
    }
  };

  return (
    <Box sx={{ maxWidth: 760, pb: 4 }}>
      <Typography component="h1" variant="h4">
        Historical data
      </Typography>
      <Typography color="text.secondary" sx={{ mt: 1 }}>
        Choose how often live layout values are captured as historical data.
        Existing history is retained when this setting changes.
      </Typography>

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mt: 3, p: 3 }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography fontWeight={700}>Capture frequency</Typography>
            <Typography color="text.secondary" variant="body2">
              The default is every 24 hours. The new frequency applies to future captures.
            </Typography>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
          {status === 'saved' && (
            <Alert severity="success">Historical data frequency updated.</Alert>
          )}

          <FormControl disabled={status === 'loading' || status === 'saving'} fullWidth>
            <InputLabel id="history-capture-frequency-label">Capture frequency</InputLabel>
            <Select
              label="Capture frequency"
              labelId="history-capture-frequency-label"
              onChange={(event) => {
                setIntervalMinutes(Number(event.target.value));
                setStatus('ready');
              }}
              value={intervalMinutes}
            >
              {HISTORY_INTERVAL_OPTIONS.map((option) => (
                <MenuItem key={option.minutes} value={option.minutes}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Button
              disabled={
                status === 'loading' ||
                status === 'saving' ||
                intervalMinutes === savedIntervalMinutes
              }
              onClick={save}
              variant="contained"
            >
              {status === 'saving' ? 'Saving...' : 'Save frequency'}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default HistoricalDataSettings;
